<template>
  <section class="flex min-w-0 flex-col gap-4" data-test-id="setup-team">
    <form class="flex flex-col gap-2" novalidate @submit.prevent="submit">
      <Label for="setup-team-email">{{ t('admin.setup.team.invite.label') }}</Label>
      <div class="flex gap-2">
        <Input
          id="setup-team-email"
          v-model="emailsRef"
          type="text"
          inputmode="email"
          autocomplete="off"
          :placeholder="t('admin.setup.team.invite.placeholder')"
          aria-describedby="setup-team-email-description"
          :aria-invalid="!!errorRef || undefined"
          data-test-id="setup-team-email-input"
        />
        <Button type="submit" :disabled="isInvitingRef" data-test-id="setup-team-invite">
          <Spinner v-if="isInvitingRef" aria-hidden="true" />
          {{ t('admin.setup.team.invite.submit') }}
        </Button>
      </div>
      <p id="setup-team-email-description" class="text-sm text-muted-foreground">
        {{ ssoOnly ? t('admin.setup.team.invite.descriptionSso') : t('admin.setup.team.invite.description') }}
      </p>
      <p v-if="errorRef" role="alert" class="text-sm text-destructive" data-test-id="setup-team-error">
        {{ errorRef }}
      </p>
    </form>

    <div v-if="isLoadingRef" class="flex justify-center py-4">
      <Spinner :aria-label="t('admin.setup.loading')" />
    </div>
    <p v-else-if="isErrorRef" role="alert" class="text-sm text-destructive">
      {{ t('admin.setup.team.errors.loading') }}
    </p>
    <ul
      v-else-if="superAdminsRef.length"
      class="flex flex-col rounded-lg border bg-card"
      :aria-label="t('admin.setup.team.list')"
      data-test-id="setup-team-list"
    >
      <li
        v-for="(admin, index) in superAdminsRef"
        :key="admin.id"
        class="flex flex-wrap items-center gap-3 px-4 py-3"
        :class="{ 'border-t': index > 0 }"
        :data-test-id="`setup-team-member-${admin.email}`"
      >
        <span
          class="flex size-8 shrink-0 items-center justify-center rounded-full text-xs font-bold"
          :class="admin.pending ? 'bg-muted text-muted-foreground' : 'bg-primary text-primary-foreground'"
          aria-hidden="true"
        >
          <MailIcon v-if="admin.pending" class="size-4" />
          <template v-else>{{ initials(admin) }}</template>
        </span>
        <span class="flex min-w-0 flex-[1_1_10rem] flex-col gap-0.5">
          <span class="flex min-w-0 items-center gap-2">
            <span class="truncate text-sm font-semibold">
              {{ displayName(admin) }}
              <span v-if="admin.isCurrentUser" class="font-normal text-muted-foreground">
                {{ t('admin.setup.team.you') }}
              </span>
            </span>
            <span
              v-if="admin.isOwner"
              class="inline-flex shrink-0 items-center gap-1 rounded-full bg-accent px-2 py-px text-xs font-semibold text-accent-foreground"
              :data-test-id="`setup-team-owner-${admin.email}`"
            >
              <CrownIcon class="size-3" aria-hidden="true" />
              {{ t('admin.setup.team.owner') }}
            </span>
            <span
              v-else-if="admin.isConstelStaff"
              class="shrink-0 rounded-full bg-muted px-2 py-px text-xs font-semibold text-muted-foreground"
            >
              {{ t('admin.setup.team.constel') }}
            </span>
          </span>
          <span class="truncate text-sm text-muted-foreground">{{ detail(admin) }}</span>
        </span>

        <!-- Ni le propriétaire ni soi-même : ils ne se retirent pas d'ici. -->
        <span v-if="!admin.isCurrentUser && !admin.isOwner" class="ml-auto flex items-center gap-1">
          <template v-if="confirmingRef === admin.id">
            <Button
              variant="destructive"
              size="sm"
              :disabled="busyRef === admin.id"
              :data-test-id="`setup-team-confirm-remove-${admin.email}`"
              @click="confirmRemove(admin)"
            >
              {{ t('admin.setup.team.remove.confirm') }}
            </Button>
            <Button variant="ghost" size="sm" @click="confirmingRef = null">
              {{ t('admin.setup.team.remove.cancel') }}
            </Button>
          </template>
          <template v-else>
            <Button
              v-if="admin.pending"
              variant="link"
              size="sm"
              class="px-0"
              :disabled="busyRef === admin.id"
              :data-test-id="`setup-team-resend-${admin.email}`"
              @click="resendTo(admin)"
            >
              {{ t('admin.setup.team.resend') }}
              <span class="sr-only">{{ admin.email }}</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              :disabled="busyRef === admin.id"
              :data-test-id="`setup-team-remove-${admin.email}`"
              @click="confirmingRef = admin.id"
            >
              {{ admin.pending ? t('admin.setup.team.remove.cancelInvitation') : t('admin.setup.team.remove.label') }}
              <span class="sr-only">{{ admin.email }}</span>
            </Button>
          </template>
        </span>
      </li>
    </ul>
  </section>
</template>

<script setup lang="ts">
import { CrownIcon, MailIcon } from 'lucide-vue-next'
import { ref } from 'vue'
import { toast } from 'vue-sonner'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import type { SuperAdminType } from '@/features/admin/platform-setup/api'
import useSuperAdmins from '@/features/admin/platform-setup/hooks/use-super-admins.hook'
import dayjsClient from '@/lib/dayjs'
import { useI18n } from '@/lib/i18n'

defineProps<{ ssoOnly?: boolean }>()

const { t } = useI18n()
const { superAdminsRef, isLoadingRef, isErrorRef, invite, isInvitingRef, resend, remove } = useSuperAdmins()

const emailsRef = ref('')
const errorRef = ref('')
const confirmingRef = ref<string | null>(null)
const busyRef = ref<string | null>(null)

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const displayName = (admin: SuperAdminType) =>
  [admin.firstname, admin.lastname].filter(Boolean).join(' ') || admin.email
const initials = (admin: SuperAdminType) =>
  ((admin.firstname[0] ?? '') + (admin.lastname[0] ?? '') || admin.email[0]).toUpperCase()

function detail(admin: SuperAdminType) {
  if (admin.pending) {
    return admin.invitedAt
      ? t('admin.setup.team.invitedAt', { when: dayjsClient(admin.invitedAt).fromNow() })
      : t('admin.setup.team.status.pending')
  }
  const hasName = !!(admin.firstname || admin.lastname)
  const login = admin.lastLoginAt
    ? t('admin.setup.team.lastLogin', { when: dayjsClient(admin.lastLoginAt).fromNow() })
    : t('admin.setup.team.neverLoggedIn')
  return hasName ? `${admin.email} · ${login}` : login
}

async function submit() {
  errorRef.value = ''
  // Plusieurs adresses possibles, séparées par des virgules, des points-virgules ou des espaces.
  const emails = emailsRef.value
    .split(/[\s,;]+/)
    .map((email) => email.trim())
    .filter(Boolean)
  if (!emails.length) {
    errorRef.value = t('admin.setup.team.errors.empty')
    return
  }
  const invalid = emails.filter((email) => !EMAIL_PATTERN.test(email))
  if (invalid.length) {
    errorRef.value = t('admin.setup.team.errors.invalid', { emails: invalid.join(', ') })
    return
  }
  try {
    const { failed } = await invite(emails)
    if (failed.length) {
      errorRef.value = t('admin.setup.team.errors.failed', { emails: failed.join(', ') })
      emailsRef.value = failed.join(', ')
      return
    }
    emailsRef.value = ''
    toast.success(t('admin.setup.team.invited', { count: emails.length }, emails.length))
  } catch {
    errorRef.value = t('admin.setup.team.errors.invite')
  }
}

async function resendTo(admin: SuperAdminType) {
  busyRef.value = admin.id
  try {
    await resend(admin.id)
    toast.success(t('admin.setup.team.resent', { email: admin.email }))
  } catch {
    toast.error(t('admin.setup.team.errors.resend'))
  } finally {
    busyRef.value = null
  }
}

async function confirmRemove(admin: SuperAdminType) {
  busyRef.value = admin.id
  try {
    await remove(admin.id)
    toast.success(t('admin.setup.team.removed', { email: admin.email }))
  } catch {
    toast.error(t('admin.setup.team.errors.remove'))
  } finally {
    busyRef.value = null
    confirmingRef.value = null
  }
}
</script>
